import React, { useEffect, useRef, useState } from "react";
import AnimatedContent from "../../common/AnimatedContent";
import PageTitle from "../../common/PageTitle";
import MeetingRequestTable from "../meetingRequest/MeetingRequestTable";
import {
  handleGetAllChairmanMeetingRequests,
  handleAcceptChairmanMeetingRequest,
  handleRejectChairmanMeetingRequest,
  handleMarkChairmanMeetingCreated,
} from "../../services/routes/chairmanMeetingRequestRoute";
import { getUserID, userName } from "../../utils/storage";
import { RiErrorWarningLine } from "react-icons/ri";
import { notifySuccess, notifyError } from "../../utils/toast";
import Loading from "../../common/Loading";
import Drawer from "../../common/Drawer";
import { FaFilter } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiCalendar } from "react-icons/fi";
import { triggerPushNotification } from "../../utils/environment";

const Field = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-gray-500 font-medium">{label}</span>
    <input
      value={value || "-"}
      disabled
      className="border border-gray-200 p-3 rounded-lg text-sm bg-gray-50 text-gray-700 w-full"
    />
  </div>
);

const TextareaField = ({ label, value, rows = 3 }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-gray-500 font-medium">{label}</span>
    <textarea
      value={value || "-"}
      disabled
      rows={rows}
      className="border border-gray-200 p-3 rounded-lg text-sm bg-gray-50 text-gray-700 w-full resize-none"
    />
  </div>
);

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value.replace ? value.replace(" ", "T") : value);
  if (isNaN(d)) return value;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const ChairmanMeeting = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [meetingData, setMeetingData] = useState([]);
  const [selectedMeetingInfo, setSelectedMeetingInfo] = useState({});
  const [selectedRequestStatus, setSelectedRequestStatus] = useState("PENDING");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [openAcceptModal, setOpenAcceptModal] = useState(false);
  const [expectedMeetingDate, setExpectedMeetingDate] = useState("");
  const [meetingLocation, setMeetingLocation] = useState("");
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [rejectedReason, setRejectedReason] = useState("");
  const [loader, setLoader] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [openMarkCreatedModal, setOpenMarkCreatedModal] = useState(false);
  const [markCreatedRecord, setMarkCreatedRecord] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  const formatDateParam = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchMeetingRequest = async (startDate = appliedStartDate, endDate = appliedEndDate) => {
    setLoading(true);
    setMeetingData([]);
    try {
      const response = await handleGetAllChairmanMeetingRequests({
        meetingStatus: selectedRequestStatus,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      });
      setMeetingData(response?.meetings || []);
    } catch (error) {
      console.error("Error fetching chairman meeting requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery("");
    fetchMeetingRequest(appliedStartDate, appliedEndDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequestStatus, appliedStartDate, appliedEndDate]);

  const handleAcceptMeeting = async () => {
    if (!expectedMeetingDate.trim()) {
      notifyError("Please provide the expected meeting date.");
      return;
    }
    if (!meetingLocation.trim()) {
      notifyError("Please provide the meeting location.");
      return;
    }
    setLoader(true);
    try {
      const dt = new Date(expectedMeetingDate);
      const pad = (n) => String(n).padStart(2, "0");
      const formatted = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
        dt.getDate()
      )} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:00.000`;
      const response = await handleAcceptChairmanMeetingRequest({
        meetingRequestId: selectedMeetingInfo.id,
        approvedByUid: getUserID(),
        expectedMeetingDate: formatted,
        location: meetingLocation,
      });
      notifySuccess(response?.message);
      // Notify the requester
      await triggerPushNotification({
        userId: selectedMeetingInfo.userUid,
        title: "Chairman Meeting Request Approved",
        body: `Your meeting request has been approved. Expected date: ${formatted.slice(0, 10)}, Location: ${meetingLocation}.`,
      });
      setOpenAcceptModal(false);
      setDrawerOpen(false);
      setExpectedMeetingDate("");
      setMeetingLocation("");
      fetchMeetingRequest();
    } catch (error) {
      notifyError(error?.response?.data || "Failed to accept request.");
    } finally {
      setLoader(false);
    }
  };

  const handleRejectMeeting = async () => {
    if (!rejectedReason.trim()) {
      notifyError("Please provide a reason for rejection.");
      return;
    }
    setLoader(true);
    try {
      const response = await handleRejectChairmanMeetingRequest({
        meetingRequestId: selectedMeetingInfo.id,
        rejectedByUid: getUserID(),
        rejectedReason,
      });
      notifySuccess(response?.message);
      // Notify the requester
      await triggerPushNotification({
        userId: selectedMeetingInfo.userUid,
        title: "Chairman Meeting Request Rejected",
        body: `Your meeting request has been rejected. Reason: ${rejectedReason}.`,
      });
      setOpenRejectModal(false);
      setDrawerOpen(false);
      setRejectedReason("");
      fetchMeetingRequest();
    } catch (error) {
      notifyError(error?.response?.data || "Failed to reject request.");
    } finally {
      setLoader(false);
    }
  };

  const handleMarkCreated = async () => {
    if (!markCreatedRecord) return;
    setLoader(true);
    try {
      const response = await handleMarkChairmanMeetingCreated({
        meetingRequestId: markCreatedRecord.id,
        personUserUid: getUserID(),
        personName: userName || "",
      });
      notifySuccess(response?.message);
      setOpenMarkCreatedModal(false);
      setMarkCreatedRecord(null);
      fetchMeetingRequest();
    } catch (error) {
      const errData = error?.response?.data;
      notifyError(typeof errData === "string" ? errData : errData?.message || "Failed to update status.");
    } finally {
      setLoader(false);
    }
  };

  const columns = [
    {
      key: "userEmployeeId",
      title: "Emp ID",
      render: (text, record) => (
        <div className="text-center">
          <span
            onClick={() => {
              setSelectedMeetingInfo(record);
              setDrawerOpen(true);
            }}
            className="text-primary font-semibold cursor-pointer hover:underline"
          >
            {record.userEmployeeId || "-"}
          </span>
        </div>
      ),
    },
    {
      key: "userName",
      title: "Name",
      render: (text, record) => (
        <div title={record.userName} className="text-center truncate font-medium">
          {(record.userName || "").replace(/\b\w/g, (c) => c.toUpperCase())}
        </div>
      ),
    },
    {
      key: "role",
      title: "Role",
      render: (t) => <div className="text-center">{t || "-"}</div>,
    },
    {
      key: "teamName",
      title: "Team",
      render: (t) => <div className="text-center">{t || "-"}</div>,
    },
    {
      key: "meetingType",
      title: "Meeting Type",
      render: (t) => <div className="text-center">{t || "-"}</div>,
    },
    {
      key: "createdTimestamp",
      title: "Created Date",
      render: (t, record) => (
        <div className="text-center whitespace-nowrap">{formatDate(record.createdTimestamp)}</div>
      ),
    },
    ...(selectedRequestStatus === "APPROVED"
      ? [
          {
            key: "approvedDate",
            title: "Approved Date",
            render: (t, record) => (
              <div className="text-center whitespace-nowrap">{formatDate(record.approvedDate)}</div>
            ),
          },
        ]
      : selectedRequestStatus === "REJECTED"
      ? [
          {
            key: "rejectedDate",
            title: "Rejected Date",
            render: (t, record) => (
              <div className="text-center whitespace-nowrap">{formatDate(record.rejectedDate)}</div>
            ),
          },
        ]
      : []),
    ...(selectedRequestStatus === "APPROVED"
      ? [
          {
            key: "meetingCreated",
            title: "Status",
            render: (t, record) => (
              <div className="flex flex-col items-center gap-1">
                <span
                  className={`text-xs font-semibold ${
                    record.meetingCreated ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {record.meetingCreated ? "Created" : "Not Created"}
                </span>
                {!record.meetingCreated && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMarkCreatedRecord(record);
                      setOpenMarkCreatedModal(true);
                    }}
                    className="bg-primary text-white text-xs px-3 py-1 rounded-full hover:opacity-90 transition whitespace-nowrap"
                  >
                    Already Scheduled ?
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      {loader && <Loading />}
      <AnimatedContent>
        <div className="flex justify-between mb-5">
          <PageTitle title="Chairman Meeting Request" />
        </div>

        {/* Underline Tabs + Search Bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-6">
            {[
              { label: "Pending", value: "PENDING" },
              { label: "Accepted", value: "APPROVED" },
              { label: "Rejected", value: "REJECTED" },
            ].map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setSelectedRequestStatus(value)}
                className={`pb-2 text-sm font-medium transition-colors ${
                  selectedRequestStatus === value
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {(appliedStartDate || appliedEndDate) && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                {appliedStartDate && (
                  <span className="bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
                    From: {appliedStartDate.split("-").reverse().join("-")}
                  </span>
                )}
                {appliedEndDate && (
                  <span className="bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
                    To: {appliedEndDate.split("-").reverse().join("-")}
                  </span>
                )}
                <button
                  onClick={() => {
                    setAppliedStartDate("");
                    setAppliedEndDate("");
                    setFilterStartDate(null);
                    setFilterEndDate(null);
                  }}
                  className="text-red-400 hover:text-red-600 font-bold text-sm"
                  title="Clear filter"
                >
                  ✕
                </button>
              </div>
            )}
            <div className="flex items-center border border-gray-300 rounded-full overflow-hidden shadow-sm bg-white w-72">
              <input
                type="text"
                placeholder="Search by name, emp ID..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="flex-1 px-4 py-2 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
              />
              <div className="bg-primary px-3 py-2 flex items-center justify-center rounded-r-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
              </div>
            </div>
            <button
              onClick={() => setFilterOpen(true)}
              className="flex items-center gap-2 bg-white border border-gray-300 text-primary px-4 py-2 rounded-full shadow-sm hover:bg-gray-50 text-sm font-medium"
            >
              <span>Filter</span>
              <FaFilter className="text-xs" />
            </button>
          </div>
        </div>

        <MeetingRequestTable
          columns={columns}
          data={meetingData.filter((m) => {
            const q = searchQuery.trim().toLowerCase();
            return (
              !q ||
              (m.userName || "").toLowerCase().includes(q) ||
              (m.userEmployeeId || "").toLowerCase().includes(q)
            );
          }).slice(0, 200)}
          loading={loading}
          limit={200}
          currentPage={1}
          nextId="next"
          prevId="prev"
          handlePageChange={() => {}}
          handlePageSize={() => {}}
          hasMore={false}
          hidePagination={true}
        />
      </AnimatedContent>

      {/* Right-side Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div
            className="absolute inset-0 bg-black bg-opacity-30"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="relative z-50 bg-white w-[420px] h-full shadow-2xl flex flex-col overflow-hidden">
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <span className="text-lg font-semibold text-primary">
                Chairman Meeting Request
              </span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full border text-gray-500 hover:bg-gray-100 text-lg leading-none"
              >
                &#x2715;
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">

              {/* Section: Employee Information */}
              <div>
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Employee Information</p>
                <div className="flex flex-col gap-3">
                  <Field label="Name"             value={selectedMeetingInfo.userName} />
                  <Field label="Employee ID"      value={selectedMeetingInfo.userEmployeeId} />
                  <Field label="Role"             value={selectedMeetingInfo.role} />
                  <Field label="Team"             value={selectedMeetingInfo.teamName} />
                  <Field label="Department"       value={selectedMeetingInfo.department} />
                  <Field label="Reporting Person" value={selectedMeetingInfo.reportingHeadName} />
                  <Field label="Phone Number"     value={selectedMeetingInfo.mobileNumber} />
                  <Field label="Email ID"         value={selectedMeetingInfo.userEmailId} />
                </div>
              </div>

              {/* Section: Meeting Overview */}
              <div>
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Meeting Overview</p>
                <div className="flex flex-col gap-3">
                  <Field label="Meeting Type"     value={selectedMeetingInfo.meetingType} />
                  <Field label="Office Location"  value={selectedMeetingInfo.officeLocation} />
                  <Field label="Designation"      value={selectedMeetingInfo.designation} />
                  <Field label="Preferred Date"   value={formatDate(selectedMeetingInfo.prefferedDate)} />
                </div>
              </div>

              {/* Section: Subject Details */}
              <div>
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Subject Details</p>
                <div className="flex flex-col gap-3">
                  <TextareaField label="Exact Purpose of Meeting" value={selectedMeetingInfo.purposeOfMeeting} rows={4} />
                  <TextareaField label="Detailed Subject"         value={selectedMeetingInfo.detailedSubject} rows={4} />
                  {selectedMeetingInfo.meetingPointsDocument && (
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500 font-medium">Meeting Points Document</span>
                      <a
                        href={selectedMeetingInfo.meetingPointsDocument}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline text-sm"
                      >
                        View Document
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Section: Meeting Parameters */}
              <div>
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Meeting Parameters</p>
                <div className="flex flex-col gap-3">
                  <Field label="Urgency Level"           value={selectedMeetingInfo.urgencyLevel} />
                  <Field label="Estimated Discussion Time" value={selectedMeetingInfo.estimatedDiscussionTime ? selectedMeetingInfo.estimatedDiscussionTime.toLowerCase() : undefined} />
                  <Field label="Business Impact Level"   value={selectedMeetingInfo.buisnessImpactLevel} />
                  <Field label="Preferred Meeting Mode"  value={selectedMeetingInfo.preferredMeetingMode} />
                </div>
              </div>

              {/* Section: Prior Discussion */}
              <div>
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Prior Discussion</p>
                <div className="flex flex-col gap-3">
                  <Field label="Have You Discussed This Earlier (Same Topic)" value={selectedMeetingInfo.earlierSameTopic ? "Yes" : "No"} />
                  {selectedMeetingInfo.earlierSameTopic && (
                    <>
                      <Field label="Last Meeting Date"    value={formatDate(selectedMeetingInfo.lastMeetingDate)} />
                      <Field label="Last Meeting Details" value={selectedMeetingInfo.lastMeetingDetails} />
                    </>
                  )}
                </div>
              </div>

              {/* Section: Meeting Outcome */}
              <div>
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Meeting Outcome</p>
                <div className="flex flex-col gap-3">
                  <TextareaField label="Expected Outcome from Meeting"        value={selectedMeetingInfo.expectedOutcomeFromMeeting} rows={2} />
                  <TextareaField label="Why Should This Be Addressed Immediately" value={selectedMeetingInfo.whyAddressImmediately} rows={2} />
                </div>
              </div>

              {/* Section: Declaration & Signature */}
              <div>
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Declaration & Signature</p>
                <div className="flex flex-col gap-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 leading-relaxed italic">
                    "I understand that Chairman Sir's meeting time is highly valuable, and I confirm that I will come prepared with all the required details, documents, and clarity related to the discussion."
                  </div>
                  <Field label="Confirmation Status" value={selectedMeetingInfo.meetingStatus === "APPROVED" ? "Confirmed" : "Not Confirmed"} />
                  {selectedMeetingInfo.signatureFile && (
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500 font-medium">Employee Signature</span>
                      <img
                        src={selectedMeetingInfo.signatureFile}
                        alt="Signature"
                        className="border rounded-lg h-20 object-contain bg-gray-50"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Section: Approval Info (APPROVED only) */}
              {selectedMeetingInfo.meetingStatus === "APPROVED" && (
                <div>
                  <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Approval Info</p>
                  <div className="flex flex-col gap-3">
                    <Field label="Approved By"           value={selectedMeetingInfo.approvedByName} />
                    <Field label="Approved Date"         value={formatDate(selectedMeetingInfo.approvedDate)} />
                    <Field label="Expected Meeting Date" value={formatDate(selectedMeetingInfo.expectedMeetingDate)} />
                    <Field label="Meeting Location"      value={selectedMeetingInfo.location} />
                  </div>
                </div>
              )}

              {/* Section: Rejection Info (REJECTED only) */}
              {selectedMeetingInfo.meetingStatus === "REJECTED" && (
                <div>
                  <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3 border-b border-gray-100 pb-1">Rejection Info</p>
                  <div className="flex flex-col gap-3">
                    <Field label="Rejected By"     value={selectedMeetingInfo.rejectedByName} />
                    <Field label="Rejected Date"   value={formatDate(selectedMeetingInfo.rejectedDate)} />
                    <Field label="Rejected Reason" value={selectedMeetingInfo.rejectedReason} />
                  </div>
                </div>
              )}

            </div>

            {/* Drawer Footer - only for PENDING */}
            {selectedMeetingInfo.meetingStatus === "PENDING" && (
              <div className="px-5 py-4 border-t flex gap-3">
                <button
                  onClick={() => setOpenAcceptModal(true)}
                  className="flex-1 bg-primary text-white rounded-full py-3 font-semibold hover:opacity-90 transition"
                >
                  Approve
                </button>
                <button
                  onClick={() => setOpenRejectModal(true)}
                  className="flex-1 bg-red-500 text-white rounded-full py-3 font-semibold hover:opacity-90 transition"
                >
                  Reject
                </button>
              </div>
            )}

            {/* Drawer Footer - toggle for APPROVED */}
            {selectedMeetingInfo.meetingStatus === "APPROVED" && (
              <div className="px-5 py-4 border-t">
                <p className="text-xs text-gray-500 mb-3 text-center">Change status</p>
                <div className="flex items-center justify-center gap-4">
                  <span className="text-sm font-medium text-gray-400">Rejected</span>
                  <button
                    onClick={() => setOpenRejectModal(true)}
                    className="relative w-14 h-7 rounded-full bg-primary transition-colors focus:outline-none"
                  >
                    <span className="absolute right-1 top-1 w-5 h-5 bg-white rounded-full shadow transition-transform" />
                  </button>
                  <span className="text-sm font-semibold text-primary">Accepted</span>
                </div>
              </div>
            )}

            {/* Drawer Footer - toggle for REJECTED */}
            {selectedMeetingInfo.meetingStatus === "REJECTED" && (
              <div className="px-5 py-4 border-t">
                <p className="text-xs text-gray-500 mb-3 text-center">Change status</p>
                <div className="flex items-center justify-center gap-4">
                  <span className="text-sm font-semibold text-red-500">Rejected</span>
                  <button
                    onClick={() => setOpenAcceptModal(true)}
                    className="relative w-14 h-7 rounded-full bg-red-500 transition-colors focus:outline-none"
                  >
                    <span className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow transition-transform" />
                  </button>
                  <span className="text-sm font-medium text-gray-400">Accepted</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Accept Modal */}
      {openAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-[500px] shadow-xl">
            <p className="text-lg font-semibold mb-4">Accept Meeting Request</p>
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Expected Meeting Date and Time{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={expectedMeetingDate}
                  onChange={(e) => setExpectedMeetingDate(e.target.value)}
                  className="border p-3 rounded-lg w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Meeting Location <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Chairman's Office, Chennai HQ"
                  value={meetingLocation}
                  onChange={(e) => setMeetingLocation(e.target.value)}
                  className="border p-3 rounded-lg w-full text-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setOpenAcceptModal(false);
                  setExpectedMeetingDate("");
                  setMeetingLocation("");
                }}
                className="flex-1 border border-gray-300 rounded-full py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAcceptMeeting}
                disabled={loader}
                className="flex-1 bg-primary text-white rounded-full py-3 text-sm font-semibold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loader ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Processing...
                  </>
                ) : "Confirm Accept"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {openRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-[500px] shadow-xl">
            <div className="text-center mb-4">
              <RiErrorWarningLine className="text-[32px] text-orange-500 mx-auto" />
              <p className="text-lg font-semibold mt-2">Reject Meeting Request</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                rows="4"
                placeholder="Enter reason for rejection..."
                value={rejectedReason}
                onChange={(e) => setRejectedReason(e.target.value)}
                className="border p-3 rounded-lg w-full text-sm"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setOpenRejectModal(false);
                  setRejectedReason("");
                }}
                className="flex-1 border border-gray-300 rounded-full py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectMeeting}
                disabled={loader}
                className="flex-1 bg-red-500 text-white rounded-full py-3 text-sm font-semibold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loader ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Processing...
                  </>
                ) : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Drawer */}
      <Drawer
        open={filterOpen}
        title="Filter"
        onClose={() => setFilterOpen(false)}
      >
        <div className="flex flex-col gap-5 pt-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
            <div className="relative w-full pr-10 border-2 border-primary rounded-md">
              <DatePicker
                selected={filterStartDate}
                onChange={(date) => { setFilterStartDate(date); setFilterEndDate(null); }}
                placeholderText="Select Start Date"
                dateFormat="dd/MM/yyyy"
                className="w-full py-2 px-3 focus:outline-none text-gray-700 text-sm"
                ref={startDateRef}
                popperPlacement="bottom-start"
              />
              <FiCalendar
                className="absolute right-3 top-3 text-gray-500 cursor-pointer"
                onClick={() => startDateRef.current?.setFocus()}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
            <div className="relative w-full pr-10 border-2 border-primary rounded-md">
              <DatePicker
                selected={filterEndDate}
                onChange={(date) => setFilterEndDate(date)}
                disabled={!filterStartDate}
                placeholderText={filterStartDate ? "Select End Date" : "Select Start Date first"}
                minDate={filterStartDate}
                dateFormat="dd/MM/yyyy"
                className="w-full py-2 px-3 focus:outline-none text-gray-700 text-sm"
                ref={endDateRef}
                popperPlacement="bottom-start"
              />
              <FiCalendar
                className="absolute right-3 top-3 text-gray-500 cursor-pointer"
                onClick={() => filterStartDate && endDateRef.current?.setFocus()}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-2">
            <button
              onClick={() => {
                setFilterStartDate(null);
                setFilterEndDate(null);
                setAppliedStartDate("");
                setAppliedEndDate("");
                setFilterOpen(false);
              }}
              className="flex-1 border border-gray-300 rounded-full py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={() => {
                if (!filterStartDate || !filterEndDate) {
                  notifyError("Please select both start and end date.");
                  return;
                }
                setAppliedStartDate(formatDateParam(filterStartDate));
                setAppliedEndDate(formatDateParam(filterEndDate));
                setFilterOpen(false);
              }}
              className="flex-1 bg-primary text-white rounded-full py-3 text-sm font-semibold hover:opacity-90"
            >
              Apply Filter
            </button>
          </div>
        </div>
      </Drawer>

      {/* Mark as Created Confirmation Modal */}
      {openMarkCreatedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-[420px] shadow-xl">
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                <RiErrorWarningLine className="text-orange-500 text-4xl" />
              </div>
              <p className="text-lg font-semibold text-gray-800">Are you sure?</p>
              <p className="text-sm text-gray-500 mt-1">
                Are you sure you want to schedule the meeting?
                {/* <span className="font-medium text-gray-700">
                  {markCreatedRecord?.userName}
                </span>? */}
              </p>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setOpenMarkCreatedModal(false);
                  setMarkCreatedRecord(null);
                }}
                className="flex-1 border border-gray-300 rounded-full py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkCreated}
                className="flex-1 bg-primary text-white rounded-full py-3 text-sm font-semibold hover:opacity-90"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChairmanMeeting;
